import l10n from '../../lang/locallang';

/**
 * AccessibleFormValidator
 *
 * Reusable client-side validation with WCAG 2.2 AA output: toggles
 * `aria-invalid` on inputs, writes each error into the field's visible
 * `#<id>-error` text (the field's aria-describedby target), and moves focus to
 * the first invalid field. The host form owns submission; this owns validation
 * + a11y.
 *
 * Announcements: the visible `#<id>-error` span is NOT a live region — announcing
 * through a describedby target while its field is focused makes screen readers
 * speak the message twice (once as the live update, once as the focused field's
 * changed description). Instead, on-the-fly errors (while typing) are spoken once
 * through a single visually-hidden live region owned by this validator. Submit /
 * server errors are announced by moving focus to the field, which reads its
 * aria-describedby error.
 *
 * Expected markup per field (see templates/Element/website/contact_form.php):
 *   <div class="js-input-validation">
 *     <input class="required" id="foo" aria-describedby="foo-error" aria-required="true">
 *     <span id="foo-error"></span>
 *   </div>
 * The <form> should carry `novalidate` so scripted validation is authoritative.
 *
 * Built-in rules run automatically: `required` (any `.required` field),
 * `email` (type="email"), `phone` (type="tel"). Field-specific rules go in
 * `customValidators`, keyed by the field's `name`; each returns an error string
 * or a falsy value, and receives `(trimmedValue, config)`.
 *
 * @example
 *   const validator = new AccessibleFormValidator(form, {
 *     customValidators: {
 *       customer_name: (value, { messages }) =>
 *         value.split(/\s+/).length < 2 ? messages.fullName : null,
 *     },
 *   });
 *   // in the host form's submit handler:
 *   if (!validator.validate()) return;
 */
export default class AccessibleFormValidator {
    constructor(form, options = {}) {
        this.form = form;
        this.config = {
            requiredClass: 'required',
            wrapperClass: 'js-input-validation',
            hasErrorClass: 'has-error',
            errorSuffix: '-error',
            messages: l10n.formValidation,
            patterns: {
                email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                phone: /^\d{10,}$/
            },
            customValidators: {},
            ...options
        };

        // Debounce timer + last-announced message per field, so typing announces
        // an error only once it changes and only after the user pauses.
        this.announceTimer = null;
        this.announced = new WeakMap();

        // Fields that have shown an error at least once. Once touched, a field is
        // re-validated on every keystroke — including back to empty — so clearing
        // a corrected field surfaces the "required" error again.
        this.touched = new WeakSet();

        // Single visually-hidden live region for on-the-fly announcements, kept
        // separate from the visible describedby error spans (see class doc).
        this.liveRegion = this.createLiveRegion();

        // Clear a field's error as soon as it is corrected (no re-submit needed).
        this.$handleInput = this.handleInput.bind(this);
        this.fields().forEach(field => field.addEventListener('input', this.$handleInput));
    }

    /** Build the visually-hidden polite live region and attach it to the form. */
    createLiveRegion() {
        const region = document.createElement('div');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.className = 'visually-hidden';
        Object.assign(region.style, {
            position: 'absolute',
            width: '1px',
            height: '1px',
            margin: '-1px',
            padding: '0',
            border: '0',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap'
        });
        this.form.appendChild(region);
        return region;
    }

    /** All fields the validator is responsible for. */
    fields() {
        return [...this.form.querySelectorAll(`.${this.config.requiredClass}`)];
    }

    /**
     * Validate every field, reflect results in the DOM/ARIA, focus the first
     * invalid field, and return whether the form is valid.
     */
    validate() {
        let firstInvalid = null;

        this.fields().forEach(field => {
            const message = this.validateField(field);
            this.setFieldError(field, message);
            if (message && !firstInvalid) {
                firstInvalid = field;
            }
        });

        if (firstInvalid) {
            firstInvalid.focus();
        }

        return !firstInvalid;
    }

    /** Return an error message for a field, or null when valid. */
    validateField(field) {
        const { patterns, messages, customValidators } = this.config;
        const value = field.value.trim();

        if (!value) {
            return messages.required;
        }
        if (field.type === 'email' && !patterns.email.test(value)) {
            return messages.email;
        }
        if (field.type === 'tel' && !patterns.phone.test(value)) {
            return messages.phone;
        }

        const custom = customValidators[field.name];
        return custom ? (custom(value, this.config) || null) : null;
    }

    /**
     * Reflect a field's validity immediately (aria-invalid + red-border class)
     * and write the message into its visible describedby error text. A falsy
     * message clears both. Used for submit/server errors, where focus also moves
     * to the field so its aria-describedby error is read (no live announce here).
     */
    setFieldError(field, message) {
        this.reflectValidity(field, message);
        this.writeVisibleMessage(field, message);
    }

    /** Set aria-invalid on the field and toggle the error class on its wrapper. */
    reflectValidity(field, message) {
        const { wrapperClass, hasErrorClass } = this.config;
        const wrapper = field.closest(`.${wrapperClass}`);
        field.setAttribute('aria-invalid', message ? 'true' : 'false');
        if (message) {
            this.touched.add(field);
        }
        if (wrapper) {
            wrapper.classList.toggle(hasErrorClass, !!message);
        }
    }

    /** Write (or clear) the field's visible error text; skip redundant writes. */
    writeVisibleMessage(field, message) {
        const errorEl = document.getElementById(`${field.id}${this.config.errorSuffix}`);
        if (!errorEl) {
            return;
        }
        const next = message || '';
        if (errorEl.textContent !== next) {
            errorEl.textContent = next;
        }
    }

    /** Speak a message once through the shared live region (clear-then-set). */
    announce(message) {
        if (!this.liveRegion || !message) {
            return;
        }
        this.liveRegion.textContent = '';
        requestAnimationFrame(() => { this.liveRegion.textContent = message; });
    }

    handleInput(e) {
        // Re-validate every field that has shown an error at least once, so the
        // message updates on any edit — including emptying a corrected field,
        // which must surface "required" again. Pristine (never-invalid) fields
        // are left alone so we don't nag before the first submit.
        const field = e.target;
        if (!this.touched.has(field)) {
            return;
        }

        const message = this.validateField(field);
        // Visual state + visible text update on every keystroke (the span is a
        // plain describedby target, so this does not announce anything).
        this.reflectValidity(field, message);
        this.writeVisibleMessage(field, message);
        clearTimeout(this.announceTimer);

        if (!message) {
            // Corrected: let the same error announce again if it recurs.
            this.announced.delete(field);
            return;
        }

        // Announce only after typing pauses, and only when the message changed.
        // Firing mid-keystroke lets the screen reader's character echo swallow it;
        // re-announcing an unchanged message on every keystroke would be noise.
        this.announceTimer = setTimeout(() => {
            const current = this.validateField(field);
            // Keep the visible text in sync with whatever we announce.
            this.writeVisibleMessage(field, current);
            if (current && current !== this.announced.get(field)) {
                this.announced.set(field, current);
                this.announce(current);
            }
        }, 700);
    }

    /** Clear all errors (e.g. after a successful submit). */
    clearErrors() {
        this.fields().forEach(field => this.setFieldError(field, null));
    }

    /**
     * Render backend validation errors (keyed by field `name`) through the same
     * mechanism as client-side ones, and focus the first one.
     */
    showServerErrors(fields = [], errors = {}) {
        let firstInvalid = null;

        fields.forEach(name => {
            const field = this.form.querySelector(`[name="${name}"]`);
            if (field) {
                this.setFieldError(field, errors[name]);
                if (!firstInvalid) {
                    firstInvalid = field;
                }
            }
        });

        if (firstInvalid) {
            firstInvalid.focus();
        }
    }
}
